import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { ArrowUpRight, CircleDot } from "lucide-react";

const KPI_TONES = {
  primary: "#4a6b53",
  warning: "#d9a05b",
  danger: "#d34949",
  info: "#4a7a8c",
};

const PRIORITY_DOTS = {
  high: "#d34949",
  medium: "#d9a05b",
  low: "#7e907a",
};

const STATUS_BADGE_CLASS = {
  done: "bg-[#eaf0ec] text-[#3d5a45]",
  in_progress: "bg-[#fdf6ed] text-[#8a5a1f]",
  todo: "bg-[#f0f0ed] text-[#575752]",
};

const NOTIFICATION_KIND_DOT = {
  success: "#4a6b53",
  warning: "#d9a05b",
  info: "#4a7a8c",
};

const KPI = ({ label, value, tone = "primary", testId }) => (
  <div
    data-testid={testId}
    className="relative overflow-hidden rounded-lg border border-[#e5e5e0] bg-white p-6"
  >
    <div
      className="absolute inset-y-0 left-0 w-[3px]"
      style={{ backgroundColor: KPI_TONES[tone] || KPI_TONES.primary }}
    />
    <div className="overline">{label}</div>
    <div className="mt-3 font-display text-4xl font-bold tracking-tight text-[#1c1c19]">
      {value}
    </div>
  </div>
);

const priorityDot = (p) => PRIORITY_DOTS[p] || PRIORITY_DOTS.low;
const statusBadge = (s) => STATUS_BADGE_CLASS[s] || STATUS_BADGE_CLASS.todo;
const notificationDot = (k) => NOTIFICATION_KIND_DOT[k] || NOTIFICATION_KIND_DOT.info;

export default function DashboardPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, t, n] = await Promise.all([
        api.get("/reports/summary"),
        api.get("/tasks"),
        api.get("/notifications"),
      ]);
      setSummary(s.data);
      setTasks(t.data.slice(0, 6));
      setNotes(n.data.slice(0, 5));
    })();
  }, []);

  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header>
        <div className="overline">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
          })}
        </div>
        <h1 className="mt-2 font-display text-4xl font-bold tracking-tight">
          Welcome back, {firstName}.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-[#575752]">
          Here’s a calm snapshot of what needs your attention today.
        </p>
      </header>

      <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KPI
          testId="kpi-headcount"
          label="Headcount"
          value={summary?.kpis.total_employees ?? "—"}
        />
        <KPI
          testId="kpi-open-tasks"
          label="Open tasks"
          value={summary?.kpis.open_tasks ?? "—"}
          tone="info"
        />
        <KPI
          testId="kpi-pending-leaves"
          label="Pending leaves"
          value={summary?.kpis.pending_leaves ?? "—"}
          tone="warning"
        />
        <KPI
          testId="kpi-total-tasks"
          label="Total tasks"
          value={summary?.kpis.total_tasks ?? "—"}
        />
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Tasks */}
        <div
          className="rounded-lg border border-[#e5e5e0] bg-white lg:col-span-8"
          data-testid="dashboard-tasks-card"
        >
          <div className="flex items-center justify-between border-b border-[#e5e5e0] px-6 py-4">
            <div>
              <div className="overline">Recent tasks</div>
              <h3 className="mt-1 font-display text-lg font-bold">
                Team assignments
              </h3>
            </div>
            <Link
              to="/app/tasks"
              className="inline-flex items-center gap-1 text-xs font-medium text-[#4a6b53] hover:text-[#3d5a45]"
            >
              View all <ArrowUpRight strokeWidth={1.5} className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="divide-y divide-[#efefe9]">
            {tasks.length === 0 ? (
              <div className="p-6 text-sm text-[#575752]">No tasks yet.</div>
            ) : (
              tasks.map((t) => (
                <div key={t.id} className="flex items-center gap-4 px-6 py-4">
                  <CircleDot
                    className="h-3.5 w-3.5 flex-none"
                    style={{ color: priorityDot(t.priority) }}
                    strokeWidth={2}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-[#1c1c19]">
                      {t.title}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-[#575752]">
                      {t.assignee_name || "Unassigned"} · due {t.due_date || "—"}
                    </div>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusBadge(t.status)}`}
                  >
                    {t.status.replace("_", " ")}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Notifications */}
        <div
          className="rounded-lg border border-[#e5e5e0] bg-white lg:col-span-4"
          data-testid="dashboard-notifications-card"
        >
          <div className="border-b border-[#e5e5e0] px-6 py-4">
            <div className="overline">Announcements</div>
            <h3 className="mt-1 font-display text-lg font-bold">What’s new</h3>
          </div>
          <ul className="divide-y divide-[#efefe9]">
            {notes.length === 0 ? (
              <li className="p-6 text-sm text-[#575752]">No announcements.</li>
            ) : (
              notes.map((n) => (
                <li key={n.id} className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: notificationDot(n.kind) }}
                    />
                    <div className="text-sm font-medium">{n.title}</div>
                  </div>
                  <div className="mt-1 text-xs leading-relaxed text-[#575752]">
                    {n.body}
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </section>
    </div>
  );
}
